import { Dispatch, FormEvent, SetStateAction, useCallback, useContext, useEffect, useState } from "react";

import {
  ObjectDocumentObjectType,
  ObjectDocumentSource,
  ProjectIssuanceTransactionConstants,
  ProjectPermissionConstants,
} from "../../../../constants";
import { PillData, ProjectIssuanceTransactionPanelData, UploadedDocument } from "../../../../models";
import { ProjectContext } from "../../../../route/shared/projects/project/ProjectContext";
import { createIssuanceTransaction } from "../../../../service/project";
import { GetProjectIssuancesResponse } from "../../../../service/query";
import { ServiceError, Status } from "../../../../service/Shared";
import { convertDateToStringDDMMYYYY, isOwnFormSubmit, useIsLoadingWrapper } from "../../../../utils";
import { useObjectAttachments } from "../../../../utils/hooks/useObjectAttachments";
import { DocumentListDocument, Toast } from "../../../../widget";
import { InteractionTag } from "../../../TagsPanel/models";
import { saveTransactionTags } from "../../utils/saveTransactionTags";

interface UseAddIssuanceTransactionPanelProps {
  show: boolean;
  issuance?: GetProjectIssuancesResponse["results"][0];
  onClose: () => void;
  handleRefresh: () => void;
}

interface UseAddIssuanceTransactionPanelReturnData {
  issuanceTransactionPanelData: ProjectIssuanceTransactionPanelData;
  setIssuanceTransactionPanelData: Dispatch<SetStateAction<ProjectIssuanceTransactionPanelData>>;
  errors: ServiceError[] | undefined;
  isHandleSubmitLoading: boolean;
  handleClosePanel: () => void;
  handleSubmit: (e: FormEvent<HTMLFormElement>) => Promise<void>;

  showTagsPanel: boolean;
  setShowTagsPanel: Dispatch<SetStateAction<boolean>>;
  changedTagsExternal: InteractionTag[];
  setChangedTagsExternal: Dispatch<SetStateAction<InteractionTag[]>>;
  setRefreshTags: Dispatch<SetStateAction<boolean>>;
  tagPills: PillData[];

  documents: DocumentListDocument[];
  canManageAttachments: boolean;
  projectUuid: string | null;
  showUploadDocumentModal: boolean;
  handleAddAttachment: () => void;
  handleCloseUploadDocumentModal: () => void;
  handleDocumentsUploaded: (documents: UploadedDocument[]) => Promise<void>;
  handleRemoveAttachment: (documentUuid: string) => Promise<void>;
  handleDownloadAttachment: (documentUuid: string) => Promise<void>;
}

export const useAddIssuanceTransactionPanel = ({
  show,
  issuance,
  onClose,
  handleRefresh,
}: UseAddIssuanceTransactionPanelProps): UseAddIssuanceTransactionPanelReturnData => {
  const { projectDetails, hasProjectPermission } = useContext(ProjectContext);

  const [errors, setErrors] = useState<ServiceError[] | undefined>();
  const [isHandleSubmitLoading, setIsHandleSubmitLoading] = useState(false);

  const [showTagsPanel, setShowTagsPanel] = useState(false);
  const [tagPills, setTagPills] = useState<PillData[]>([]);
  const [changedTagsExternal, setChangedTagsExternal] = useState<InteractionTag[]>([]);
  const [refreshTags, setRefreshTags] = useState(false);

  const [showUploadDocumentModal, setShowUploadDocumentModal] = useState(false);
  const [uploadToken, setUploadToken] = useState(0);

  const [issuanceTransactionPanelData, setIssuanceTransactionPanelData] = useState<ProjectIssuanceTransactionPanelData>(
    {
      ...ProjectIssuanceTransactionConstants.DEFAULT_ISSUANCE_TRANSACTION,
      issuanceUuid: issuance?.uuid ?? null,
    }
  );

  /*
    Add is only reachable from a project's inventory, so the project a document is uploaded against is the one
    in context. The issuance itself carries no project uuid.
  */
  const projectUuid = projectDetails?.uuid ?? null;
  const canManageAttachments = hasProjectPermission?.(ProjectPermissionConstants.MANAGE_PROJECT_ISSUANCES) ?? false;

  /*
    The transaction does not exist yet, so uploads are staged against the panel and linked by saveAttachments
    once the create returns a uuid.
  */
  const {
    attachments,
    beginUpload,
    handleDocumentsUploaded: linkUploadedDocuments,
    handleRemoveAttachment,
    handleDownloadAttachment,
    saveAttachments,
    resetAttachments,
  } = useObjectAttachments({
    objectUuid: undefined,
    objectType: ObjectDocumentObjectType.ISSUANCE_TRANSACTION,
    documentSource: ObjectDocumentSource.PROJECT_DOCUMENT,
    documentScope: "project",
    parentUuid: projectUuid,
    subjectLabel: "transaction",
  });

  const documents: DocumentListDocument[] = attachments.map((attachment) => ({
    documentUuid: attachment.documentUuid,
    fileName: attachment.fileName,
    documentType: attachment.documentType ?? undefined,
    uploadedAt: attachment.uploadedAt ? convertDateToStringDDMMYYYY(attachment.uploadedAt) : undefined,
    url: attachment.url ?? undefined,
  }));

  const handleClosePanel = useCallback((): void => {
    onClose();
    setIssuanceTransactionPanelData({
      ...ProjectIssuanceTransactionConstants.DEFAULT_ISSUANCE_TRANSACTION,
    });
    setShowUploadDocumentModal(false);
    setTagPills([]);
    setChangedTagsExternal([]);
    setErrors([]);
    resetAttachments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose]);

  const handleAddAttachment = (): void => {
    setUploadToken(beginUpload());
    setShowUploadDocumentModal(true);
  };

  const handleCloseUploadDocumentModal = (): void => {
    setShowUploadDocumentModal(false);
  };

  const handleDocumentsUploaded = async (uploaded: UploadedDocument[]): Promise<void> => {
    setShowUploadDocumentModal(false);
    await linkUploadedDocuments(uploaded, uploadToken);
  };

  const handleSubmit = useIsLoadingWrapper(async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!isOwnFormSubmit(e)) return;
    setErrors([]);

    const res = await createIssuanceTransaction({
      issuanceTransactions: [
        {
          areaHa: issuanceTransactionPanelData.areaHa,
          counterPartyCollaboratorUuid: issuanceTransactionPanelData.counterPartyCollaboratorUuid,
          brokerCollaboratorUuid: issuanceTransactionPanelData.brokerCollaboratorUuid,
          expiryDate: issuanceTransactionPanelData.expiryDate,
          externalReference: issuanceTransactionPanelData.externalReference,
          fees: issuanceTransactionPanelData.fees,
          issuanceUuid: issuanceTransactionPanelData.issuanceUuid ?? "",
          parentTransactionUuid: issuanceTransactionPanelData.parentTransactionUuid,
          price: issuanceTransactionPanelData.price,
          quantity: issuanceTransactionPanelData.quantity ?? 0,
          totalValue:
            issuanceTransactionPanelData.totalValue !== null
              ? parseFloat(issuanceTransactionPanelData.totalValue.toFixed(5))
              : null,
          transactionDate: issuanceTransactionPanelData.transactionDate,
          type: issuanceTransactionPanelData.type ?? "",
        },
      ],
    });

    if (res.status === Status.Error) {
      setErrors(res.errors);
      return;
    }

    const createdTransactionUuid = res.data?.issuanceTransactions?.[0]?.uuid;

    const tagErrors = createdTransactionUuid
      ? await saveTransactionTags(createdTransactionUuid, changedTagsExternal)
      : undefined;
    const attachmentErrors = createdTransactionUuid ? await saveAttachments(createdTransactionUuid) : undefined;

    Toast.success({ message: "Issuance transaction created successfully" });

    /*
      Reported after the success rather than instead of it: the transaction is already persisted, so each of
      these is a partial failure on top of a create that did happen.
    */
    if (tagErrors) {
      Toast.error({ message: `The transaction was saved, but its tags could not be added: ${tagErrors[0].message}` });
    }

    if (attachmentErrors) {
      Toast.error({
        message: `The transaction was saved, but its attachments could not: ${attachmentErrors[0].message}`,
      });
    }

    handleRefresh();
    handleClosePanel();
  }, setIsHandleSubmitLoading);

  useEffect(() => {
    setIssuanceTransactionPanelData({
      ...ProjectIssuanceTransactionConstants.DEFAULT_ISSUANCE_TRANSACTION,
      issuanceUuid: issuance?.uuid ?? null,
    });
  }, [issuance?.uuid, show]);

  useEffect(() => {
    if (!show) return;
    setTagPills(changedTagsExternal.filter((x) => x.checked).map((x) => ({ key: x.uuid, label: x.data.name })));
  }, [show, refreshTags, changedTagsExternal]);

  return {
    errors,
    isHandleSubmitLoading,
    issuanceTransactionPanelData,
    setIssuanceTransactionPanelData,
    handleClosePanel,
    handleSubmit,
    showTagsPanel,
    setShowTagsPanel,
    setChangedTagsExternal,
    changedTagsExternal,
    setRefreshTags,
    tagPills,
    documents,
    canManageAttachments,
    projectUuid,
    showUploadDocumentModal,
    handleAddAttachment,
    handleCloseUploadDocumentModal,
    handleDocumentsUploaded,
    handleRemoveAttachment,
    handleDownloadAttachment,
  };
};
